<template>
<div class="chapter">
<div class="inner_page">
<h2 class="h2"><a id="page_6"></a><a id="ch01"><span class="txbfit">1<br/>Deep into That
                    Darkness</span></a></h2><a id="ch01">
<p class="noindent">“Okay, Hawks,” Coach Anne said. “That’s a wrap.
            We can officially call that our last run before Nationals. At least until we hit
            Dallas.”</p>
<p class="indent">Isobel released a sigh, her shoulders slumping in relief.</p>
<p class="indent">Around her, tired whoops and clapping echoed through the gym, everyone
            breaking off to find their water bottles and towels.</p>
<p class="indent">A dull ache spread its way slowly through her as she allowed her muscles
            to unclench. Her whole body felt like a twisted rope unwinding.</p>
<p class="indent">Already, Coach had drilled the routine at least twenty times. Even if
            Coach <span class="txit">had</span> wanted them to go again, Isobel didn’t think
            she could have managed another pike basket toss, let alone landed one more full.</p>
<p class="indent">She knew she wasn’t the only one running on fumes either.</p>
<p class="indent">She’d felt the entire squad’s energy draining away little by
            little, like a machine operating on a single dying battery.</p>
<p class="indent">Coach must have felt it too. Isobel had no doubt that she would have
            drilled them until midnight if she hadn’t sensed her squad preparing for
            mutiny.</p>
</a><p class="indent"><a id="ch01"></a><a id="page_7">Then again, it wasn’t unusual for Coach to pull
            this kind of boot-camp, cheer-till-you-drop drillathon, especially right before a big
            competition. And this was <span class="txit">the</span> competition, after all. But her
            motivation for killing them like this lay less, Isobel knew, in ironing out any
            last-minute kinks and more in sending everyone home too tired to do anything but
            crash.</a></p><a id="page_7">
<p class="indent">“I want you all to get some <span class="txit">rest</span>
            tonight,” Coach shouted above a sudden burst of laughing and talking, her words
            confirming Isobel’s suspicions. “That means no late-night Facebook updates,
            no texting, no two a.m. phone calls with Mr. or Miss Flavor of the Week, and <span class="txit">no</span> last-minute stunting in the living room—I’m
            talking to you, Miss Dorbon. I want everybody here in one piece and ready to go at five
            a.m. <span class="txit">sharp.</span> Got that?” Coach lifted one thick arm over
            her frizzy poof of brown hair and pointed at her wristwatch. “Bus leaves at <span class="txit">six</span> on the dot, so set your alarms. No hitting the snooze button
            forty times. No ‘I forgot my uniform.’ No excuses. I know I don’t have
            to tell any of you that we won’t wait if you’re late.”</p>
<p class="indent">Speaking of late, Isobel wondered what time it was. It felt like
            they’d been there for hours.</p>
<p class="indent">She glanced above the gym doors to the white-faced clock secured behind
            the protective metal grate designed to shield it from foul balls.</p>
<p class="indent">At the sight of the dark, familiar figure standing in the doorway,
            however, all thoughts of time flew from her mind.</p>
<p class="indent">Hands stuffed into the pockets of his black jeans, he watched her from
            behind reflective sunglasses, his expression calm, blank.</p>
</a><p class="indent"><a id="page_7"></a><a id="page_8">A panicked stirring arose inside her, coupled with a
            nagging sensation that tugged at the back of her mind, like a child pulling on the hem
            of her mother’s dress. It was as though some deeper part of her was trying
            desperately to get her attention.</a></p><a id="page_8">
<p class="indent">Behind her, Isobel could hear Coach Anne’s continued tirade as she
            rattled off reminders about their uniforms and which colored tennis shoe inserts to
            wear. Blue bows for hair this time, she droned, not yellow. A-line skirts, <span class="txit">not</span> pleated.</p>
<p class="indent">The longer Isobel stared at the figure standing in the open doorway,
            though, the more distant Coach’s voice began to grow. The walls of the gymnasium,
            the squad, and the floor, too—they all blurred out of her vision until there was
            only him.</p>
<p class="indent">Isobel walked toward the figure and reached for the glasses, the urge to
            strip them from his face and look into his eyes nothing short of a compulsion.</p>
<p class="indent">He stopped her hand with his. The contact made her pause, and the nameless
            dread inside her melted away as his fingers intertwined with hers.</p>
<p class="indent">His hand felt so warm.</p>
<p class="indent">“Ready to go?” he asked.</p>
<p class="indent">His voice rippled through her, low, soft, and a little husky—like
            the hushed crackling of an old-fashioned record player just before the music starts.
            Quieting the tangled mesh of her thoughts, it numbed her like a drug.</p>
<p class="indent">Her eyes flicked down from his glasses to the slight smile that tugged at
            one corner of his mouth. A glint of light caught on his lip ring, causing the silver to
            flash.</p>
</a><p class="indent"><a id="page_8"></a><a id="page_9">Suddenly it was too hard to breathe. She wanted to feel
            that tiny slip of metal against her own lips, to kiss him. As if that would somehow help
            her catch her breath.</a></p><a id="page_9">
<p class="indent">But she couldn’t escape the feeling that there was something about
            the moment, something about his very <span class="txit">presence</span> that she
            wasn’t grasping. It was as if her mind had misplaced some vital bit of
            information. Or lost it entirely.</p>
<p class="indent">“What—what are you doing here?” she asked him, because
            it was the one question that kept pushing all the others out of the way.</p>
<p class="indent">One of his eyebrows drifted above the top edge of his sunglasses. His half
            smile remained in place. “I came to pick you up,” he said.
            “You’re my girlfriend. I do that now, remember?”</p>
<p class="indent"><span class="txit">Girlfriend.</span></p>
<p class="indent">The word felt like a switchblade to her heart. The pain it evoked was more
            tender than sharp, though, the kind that comes along with saying good-bye to a friend
            you know you’ll never see again.</p>
<p class="indent">“C’mon,” he said before she could ask any more
            questions. He began to turn away and she felt his hand tighten around hers, squeezing,
            tugging her after him. “We should go.”</p>
<p class="indent">Isobel found herself following him, her steps falling in stride with
            his.</p>
</a><p class="indent"><a id="page_9">She wanted to look back, to see who’d been watching and who had
            noticed. Certainly Coach had seen her go. Isobel couldn’t understand why Coach
            wasn’t yelling at her right that very second, shouting for her to come back and
            that </a><a id="page_10">practice wasn’t over until after cooldown. But she
            didn’t have time to turn around. She and Varen had already reached the double
            doors that led out into the school’s rear parking lot.</a></p><a id="page_10">
<p class="indent">They pushed through, greeted by a cascade of snow that poured from above,
            the gray-purple clouds all but blotting out the sky, leaving no room for the cold winter
            sun.</p>
<p class="indent">Varen’s black 1967 Cougar sat alone in the empty parking lot, a dark
            inkblot surrounded by a sea of vacant whiteness.</p>
<p class="indent">Isobel frowned. Where were all the other cars? Where was the line of
            minivans and SUVs waiting to pick up the rest of the squad? Where was Coach’s
            hulking, rust-red Suburban?</p>
<p class="indent">“I need to show you something,” she heard Varen say, though he
            didn’t turn around.</p>
<p class="indent">Isobel’s focus narrowed in on the nape of his neck, the place where
            his hair, black and silken, jagged as crows’ feathers, brushed the collar of his
            T-shirt.</p>
<p class="indent">Had she only just noticed how long it had grown?</p>
<p class="indent">A breeze whipped past them, and his bare arms made her wonder why he
            hadn’t worn his jacket.</p>
<p class="indent">“Varen, where are we going?”</p>
<p class="indent">“You’ll see,” was his only response as he hurried her
            through the parking lot. Beneath their feet, the snow, still fresh and powdery, made no
            sound.</p>
<p class="indent">Reaching the Cougar, he opened and held the passenger-side door for her,
            the cab light illuminating the familiar burgundy interior.</p>
</a><p class="indent"><a id="page_10">She hesitated and glanced back to Varen. Shifting his weight from one foot
            to the other, he gestured to the upholstery. </a><a id="page_11">“Yeah,” he
            said, “sorry about that. Still waiting on those mink seat covers.”</a></p><a id="page_11">
<p class="indent">Isobel shot him a wry smile. Before she could return his trademark sarcasm
            with her own dry quip, though, something about his appearance made her pause.</p>
<p class="indent">There was something missing. Something off . . .</p>
<p class="indent">She realized that even though she was looking straight at him, she could
            not see herself in the mirrored lenses of his glasses, only the reflection of blackened
            trees standing in rows behind her, their thin prison-bar trunks still visible through
            the thickening screen of falling snow.</p>
<p class="indent">In the reflection, a large ebony bird lifted off from one of the twisted
            branches, and the sound of its beating wings caused her to flinch and whirl. But when
            she looked, there were no trees. No bird. Only the rigid outline of Trenton High’s
            neo-Gothic facade.</p>
<p class="indent">From here, Isobel could just make out the four spires of the
            school’s main entrance tower peeking up over the roof’s ledge. The countless
            windowpanes glared white, refracting the overcast light like a thousand dead eyes. Even
            though she’d just left the squad in the gym, the entire building now appeared
            deserted—except for the top floor, where Isobel thought she saw the silhouette of
            someone standing in one of the windows, watching them.</p>
<p class="indent">“Get in,” Varen said. “Now.”</p>
<p class="indent">Isobel turned and sank into the car, spurred by the urgency in his
            voice.</p>
</a><p class="indent"><a id="page_11">She shut the door behind her and, glancing to the driver’s </a><a id="page_12">side, was shocked to find him already there, one hand on the
            steering wheel, the other locked around the stick shift, the bulky onyx gem of his class
            ring shining like oil in the stark light.</a></p><a id="page_12">
<p class="indent">The car hummed. Isobel felt her seat vibrate beneath her as the engine
            rumbled, though she couldn’t recall his turning the key. The smell of exhaust
            fumes filtered into her awareness while the windshield wipers jumped into action,
            slashing back and forth to cast off the gathering snow. By now, the cascade of whiteness
            had grown so heavy that the world outside had all but vanished.</p>
<p class="indent">Beside her on the seat, the ratty old Discman Varen had rigged up to the
            Cougar’s dashboard radio sprang to life. Through the tiny rectangular window,
            Isobel saw the disc inside whir. A woman’s soft voice erupted through a hiss of
            static. Her humming, unaccompanied by any instrumentals, filled the car. Soft and sweet,
            sad but beautiful, the naked melody was one Isobel had never heard before. The voice,
            too, was unfamiliar, possessing an airy quality, wispy and almost shy.</p>
<p class="indent">Without warning, Varen snatched the Discman, yanking it free from the
            wires that connected it to the dashboard, instantly silencing the static and the
            humming. With a hard scowl, he tossed the CD player into the backseat. Grabbing the
            stick shift again, he threw the car into gear.</p>
<p class="indent">His foot hit the gas pedal and they began to move, accelerating to top
            speed. Isobel opened her mouth to speak, but Varen cut her off, turning the wheel
            sharply.</p>
</a><p class="indent"><a id="page_12"></a><a id="page_13">Pressed to the passenger-side door, she groped for
            something to hold on to, instantly reminded of that night he’d driven her home,
            careening down the road while ignoring her desperate pleas for him to stop. Fear erupted
            inside her like a match striking. She gripped the seat beneath her, able only to see
            endless white through the windows.</a></p><a id="page_13">
<p class="indent">“Varen! You can’t even see where you’re
            going!”</p>
<p class="indent">“I don’t have to,” he said.</p>
<p class="indent">Isobel felt her muscles tighten again, coiling up, tensing in preparation
            for the impact that would surely kill them both any second.</p>
<p class="indent">“Varen! Ple—” Isobel stopped, her words evaporating in
            her mouth as she caught sight of the small clock embedded into the dashboard.</p>
<p class="indent">The hands of the clock looped opposite each other and spun lazily around
            and around, never stopping. She watched the needle of the speedometer tip to and fro
            like the pendulum of a metronome.</p>
<p class="indent">The gas gauge read empty, but she could <span class="txit">hear</span> the
            engine growling, guzzling fuel.</p>
<p class="indent">“Wait,” she whispered, more to herself than to him.
            “This isn’t . . . This is a dr—”</p>
<p class="indent"><span class="txit">“Don’t,”</span> he snapped, silencing
            her. “Not yet.”</p>
<p class="indent">His foot slammed on the brake. Isobel pitched forward in her seat as the
            car skidded to a halt, its tires shrieking.</p>
<p class="indent">Like sand being blown from a relic, the snow coating the windshield began
            to erode.</p>
<p class="indent">Or rather, Isobel thought, the <span class="txit">ash.</span></p>
</a><p class="indent"><a id="page_13"></a><a id="page_14">Tiny blots of bright crimson now fluttered down all
            around them, lighting on the windshield.</a></p><a id="page_14">
<p class="indent">The car jerked to a final stop, causing Isobel to fly back again. Frantic,
            she turned toward the driver’s side, only to find the car door flung open and
            Varen gone.</p>
<p class="indent">Outside, countless red roses bobbed their heads, their waxy leaves
            rustling in a sudden gust of wind that sent even more blood-colored petals raining over
            the car.</p>
<p class="indent">Thick and heady, the aroma of the blossoms weighed down the air.</p>
<p class="indent">Isobel fumbled for the handle on her door, which popped open as soon as
            she touched the latch. Jerking her arm with it, the door swung out wide over the jagged
            edge of a black cliff.</p>
<p class="indent">Far below, milky waters churned amid toothy rocks while the waves clamored
            one over the other, snapping like white wolves before smashing against the flat face of
            the cliff.</p>
<p class="indent">Isobel gave a silent shriek. She backpedaled for the driver’s side.
            Twisting, she grabbed hold of the steering wheel, using it to pull herself out on the
            other side.</p>
<p class="indent">She spilled hard onto the ground. Rolling onto her back, she hiked one
            knee up and kicked, sending the door of the Cougar slamming shut.</p>
<p class="indent">The echoing clap caused the car to disperse into ashes.</p>
<p class="indent">Isobel raised her arms to shield her face from the spray of grit. Through
            the settling dust, she saw that the cliffs had vanished, leaving only the surrounding
            walls of ruby blossoms.</p>
</a><p class="indent"><a id="page_14">In the midst of what appeared to be an enormous rose </a><a id="page_15">garden, a familiar structure became discernible through the screen of the settling
            powder.</a></p><a id="page_15">
<p class="indent">Isobel recognized the structure as the fountain from Varen’s
            neighborhood. It now stood in the center of a circular dome-shaped room enclosed by
            scarlet blooms.</p>
<p class="indent">Without the curtain of crystal water pouring from the ledge of its rounded
            green basin, the fountain was a silent and eerie monument.</p>
<p class="indent">Isobel pulled herself to her feet, her practice sneakers caked with ash,
            chalk white against a carpet of ruby petals.</p>
<p class="indent">Her eyes locked on the statue of the woman that stood at the top of the
            fountain, her stiff stone veil clutched in her hands, the fabric arcing out behind her
            nearly nude figure in a backward C.</p>
<p class="indent">Isobel turned in a circle. All around her, buds and blossoms in various
            states of unfurling dotted the trellised walls. High over the statue’s head, thick
            vines met at a circular opening at the top of the domed ceiling. Through the porthole,
            she could see a tangled webwork of black tree limbs.</p>
<p class="indent">What was this place?</p>
<p class="indent">And where was—?</p>
<p class="indent">“Varen!”</p>
<p class="indent">“Here.”</p>
<p class="indent">Isobel started, nearly yelping as she found him standing right in front of
            her.</p>
<p class="indent">She peered up into eyes no longer shielded by sunglasses. Their centers
            were black, swept clean of color and light.</p>
</a><p class="indent"><a id="page_15">She searched through their darkness, desperate to find </a><a id="page_16">some irrefutable evidence in their depths that could prove it was really him.</a></p><a id="page_16">
<p class="indent">“Is—is any of this real?” she asked. “Are <span class="txit">you</span> real?”</p>
<p class="indent">He lifted a hand to her cheek, his fingers brushing her jaw.</p>
<p class="indent">“Even if this is a dream,” he whispered, “I’m
            not.”</p>
<p class="indent">Isobel’s eyes widened, recognizing those words as her own, the same
            ones she had once uttered to him. She reached for him, her arms twining around his neck,
            drawing him to her so that his scent poured over her, that combination of incense,
            citrus, and dried leaves overriding the funeral smell of the crowding flowers.</p>
<p class="indent">He lowered his forehead to hers, his hair draping around their faces, the
            smooth strands tickling her skin.</p>
<p class="indent">“Don’t leave,” she breathed.</p>
<p class="indent">“I’m here,” he whispered. “Right here.
            Waiting.”</p>
<p class="indent">He leaned in.</p>
<p class="indent">Isobel tilted her chin up, ready for the press of his lips.</p>
<p class="indent">She wanted to let her eyes fall shut, but something, a sensation of being
            watched, stopped her. Her glance slid past his shoulder, her focus drawn to the statue
            atop the fountain.</p>
<p class="indent">Between the inky strands of Varen’s hair, Isobel watched its eyes
            slide open. She stared, transfixed, as the statue turned its head toward them, aiming
            those two empty pits of blackness straight at her.</p>
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ch01.vue?d3a78860